import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import api from '@/lib/api';
import { Card, Button, Skeleton } from '@/components/ui';

interface Animal {
  id: number;
  name: string;
  name_latin: string;
  slug: string;
  image_url: string;
  short_description: string;
}

interface CategoryData {
  category: {
    id: number;
    name: string;
    slug: string;
    description: string;
    icon: string;
    image_url: string;
  };
  animals: Animal[];
}

export function Category() {
  const { t, i18n } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  // Include lang in queryKey so cache is invalidated on language switch
  const lang = i18n.language?.startsWith('en') ? 'en' : 'ru';

  const { data, isLoading, error } = useQuery({
    queryKey: ['category', slug, lang],
    queryFn: async () => {
      const { data } = await api.get<CategoryData>(`/api/categories/${slug}`);
      return data;
    },
    enabled: !!slug,
  });

  if (isLoading) {
    return (
      <div className="relative z-10 space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-96" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <Skeleton key={i} className="h-64" />
          ))}
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="relative z-10 text-center py-12">
        <span className="text-6xl mb-4 block">😿</span>
        <h1 className="text-2xl font-bold mb-2">{t('category.notFound')}</h1>
        <p className="text-gray-400 mb-6">{t('category.notFoundHint')}</p>
        <Link to="/">
          <Button>{t('common.returnToHome')}</Button>
        </Link>
      </div>
    );
  }

  const { category, animals } = data;

  return (
    <div className="relative z-10 space-y-6">
      {/* Back button */}
      <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors">
        <ArrowLeft className="w-4 h-4" />
        <span>{t('common.back')}</span>
      </Link>

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center gap-4"
      >
        <span className="text-5xl">{category.icon}</span>
        <div>
          <h1 className="text-3xl font-bold">{category.name}</h1>
          <p className="text-gray-400">{category.description}</p>
        </div>
      </motion.div>

      {/* Animals count */}
      <p className="text-sm text-gray-500">
        {t('category.animalsFound')}: {animals.length}
      </p>

      {/* Animals grid */}
      {animals.length === 0 ? (
        <div className="text-center py-12">
          <span className="text-6xl mb-4 block">🔍</span>
          <p className="text-gray-400">{t('category.noAnimals')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {animals.map((animal, index) => (
            <motion.div
              key={animal.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Link to={`/animal/${animal.slug}`}>
                <Card
                  image={animal.image_url}
                  imageAlt={animal.name}
                  hover
                  className="h-full"
                >
                  <h3 className="font-semibold text-white">{animal.name}</h3>
                  <p className="text-sm text-gray-500 italic mb-2">
                    {animal.name_latin}
                  </p>
                  <p className="text-sm text-gray-400 line-clamp-2">
                    {animal.short_description}
                  </p>
                </Card>
              </Link>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
